import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  KeyRound, ArrowRight, Loader2, Hourglass, Mail, Phone, RefreshCw,
  Sparkles, Gift, ChevronDown, Fingerprint, Zap,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { NovatusLogo } from "@/components/NovatusLogo";
import { PhoneInput } from "@/components/PhoneInput";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { isPasskeySupported, loginWithPasskey } from "@/lib/passkey";
import { postLoginDestination } from "@/lib/postLoginRoute";

// Demo test accounts — credentials are PHONE-based but we display them clearly
// so a user can also see which role they will impersonate. Click pre-fills the
// active input (phone or email) with the account's identifier.
// Telephone-only quick-test login UI was removed for security reasons.
// Demo phone numbers (still working but no longer exposed in the UI):
//   +491700000001 — Lena Meyer (Vertrieb)
//   +491700000002 — Markus Berger (Vertriebsleiter)
//   +491700000003 — Peter Klein (Vertriebsdirektor)
//   +491700000004 — Paul Weber (Installation)

const roleLabelMap = {
  super_admin: "Super-Admin",
  admin: "Administrator",
  sales_director: "Vertriebsdirektor",
  sales_manager: "Vertriebsleiter",
  branch_manager: "Niederlassungsleiter",
  sales_rep: "Vertrieb",
  telesales_director: "Telesales-Direktor",
  agent: "Telesales-Agent",
  installer: "Installation",
};

// Geheime „Magic-Number" zum Aufdecken der Demo-Schnellzugänge.
// Solange der User diese Zahl nicht in das Telefonfeld eingibt, bleibt
// die Demo-Liste komplett versteckt — schützt das Produktivsystem vor
// öffentlich sichtbaren Test-Logins. Überschreibbar via Env.
const DEMO_UNLOCK_CODE = (process.env.REACT_APP_DEMO_UNLOCK_CODE || "31122026000000")
  .replace(/\D/g, "");
const _matchesDemoUnlock = (raw) => {
  const digits = (raw || "").replace(/\D/g, "");
  if (!digits || !DEMO_UNLOCK_CODE) return false;
  return digits === DEMO_UNLOCK_CODE || digits.endsWith(DEMO_UNLOCK_CODE);
};

export default function Login() {
  const [mode, setMode] = useState("email"); // 'phone' | 'email' — default email; flips to phone-allowed once config confirms SMS.
  // Optimistic default: SMS is on unless `/auth/config` explicitly reports
  // it disabled. Prevents a transient network glitch on first page-load (e.g.
  // cold-start, Service Worker boot, slow mobile) from locking the user out
  // of phone-mode login when SMS is actually available on the backend.
  const [smsEnabled, setSmsEnabled] = useState(true);
  const [step, setStep] = useState("identify"); // identify | otp
  const [phone, setPhone] = useState("+49");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [mockOtp, setMockOtp] = useState("");
  const [emailMasked, setEmailMasked] = useState("");
  const [phoneMasked, setPhoneMasked] = useState("");
  const [flow, setFlow] = useState(""); // 'login' | 'register' | 'pending' | 'blocked'
  const [pendingFirstName, setPendingFirstName] = useState("");
  const [loading, setLoading] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const passkeysAvailable = isPasskeySupported();
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownTimer = useRef(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  const phoneValid = phone.replace(/\D/g, "").length >= 10;
  const emailValid = /^\S+@\S+\.\S+$/.test(email);
  const canSubmit = mode === "phone" ? phoneValid : emailValid;

  const startCooldown = (seconds = 30) => {
    setResendCooldown(seconds);
    if (cooldownTimer.current) clearInterval(cooldownTimer.current);
    cooldownTimer.current = setInterval(() => {
      setResendCooldown((s) => {
        if (s <= 1) {
          clearInterval(cooldownTimer.current);
          cooldownTimer.current = null;
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    // Fetch auth-config to find out whether SMS-OTP is currently enabled.
    // Retry on transient errors (cold-start, Service Worker warmup) so a
    // brief network glitch doesn't lock the user out of phone-mode login.
    let cancelled = false;
    const fetchConfig = async (attempt = 0) => {
      try {
        const r = await api.get("/auth/config");
        if (cancelled) return;
        const enabled = !!r.data?.sms_enabled;
        setSmsEnabled(enabled);
        if (!enabled) setMode("email");
      } catch {
        if (cancelled) return;
        if (attempt < 2) {
          setTimeout(() => fetchConfig(attempt + 1), 800 * (attempt + 1));
        }
        // After final failure we keep the optimistic default (smsEnabled=true);
        // backend will reject the call cleanly if SMS is truly disabled.
      }
    };
    fetchConfig();
    return () => {
      cancelled = true;
      if (cooldownTimer.current) clearInterval(cooldownTimer.current);
    };
  }, []);

  // --- WebOTP API: Android Chrome can auto-pick the code from the incoming
  //     SMS (the SMS body includes the required "@host #code" token).
  //     iOS Safari uses the `autocomplete="one-time-code"` attribute on the
  //     input instead, which is set declaratively below.
  useEffect(() => {
    if (step !== "otp") return undefined;
    if (typeof navigator === "undefined" || !("OTPCredential" in window)) {
      return undefined;
    }
    const ac = new AbortController();
    navigator.credentials
      .get({ otp: { transport: ["sms"] }, signal: ac.signal })
      .then((cred) => {
        if (cred && cred.code) {
          setOtp(String(cred.code).replace(/\D/g, "").slice(0, 6));
          toast.success("Code automatisch aus SMS übernommen");
        }
      })
      .catch(() => {
        // Aborted or user denied — silent.
      });
    return () => ac.abort();
  }, [step]);

  const start = async (override = null) => {
    const payload = override || (mode === "phone" ? { phone } : { email });
    if (!override && !canSubmit) {
      toast.error(mode === "phone" ? "Bitte eine gültige Mobilnummer eingeben" : "Bitte eine gültige E-Mail-Adresse eingeben");
      return;
    }
    setLoading(true);
    setPendingFirstName("");
    setMockOtp("");
    setEmailMasked("");
    setPhoneMasked("");
    try {
      const r = await api.post("/auth/start", payload);
      const d = r.data;
      if (d.flow === "pending") {
        setPendingFirstName(d.first_name || "");
        setFlow("pending");
        return;
      }
      if (d.flow === "register") {
        // Unknown identifier → OTP sent. Show OTP step first; after
        // verification the user is sent to the profile-creation page.
        setFlow("register");
        setMockOtp(d.mock_otp || "");
        setEmailMasked(d.email_masked || "");
        setPhoneMasked(d.phone_masked || "");
        setStep("otp");
        setOtp("");
        startCooldown(30);
        if (d.delivery === "on_screen") {
          toast.success("Demo-Zugang — Code wird direkt angezeigt");
        } else if (d.delivery === "sms+email") {
          toast.success("Code per SMS und E-Mail gesendet", {
            description: `An ${d.phone_masked || "Ihre Nummer"} und ${d.email_masked || "Ihre E-Mail"}`,
          });
        } else if (d.delivery === "sms") {
          toast.success("Code per SMS gesendet", {
            description: d.phone_masked ? `An ${d.phone_masked}` : "Bitte SMS-Eingang prüfen",
          });
        } else if (d.delivery === "email") {
          toast.success("Code per E-Mail gesendet", {
            description: d.email_masked ? `An ${d.email_masked}` : "Bitte Postfach prüfen",
          });
        }
        return;
      }
      // login flow
      setFlow("login");
      setMockOtp(d.mock_otp || "");
      setEmailMasked(d.email_masked || "");
      setPhoneMasked(d.phone_masked || "");
      setStep("otp");
      setOtp("");
      startCooldown(30);
      if (d.delivery === "on_screen") {
        toast.success("Test-Zugang — Code wird direkt angezeigt");
      } else if (d.delivery === "sms+email") {
        toast.success("Code per SMS und E-Mail gesendet", {
          description: `An ${d.phone_masked || "Ihre Nummer"} und ${d.email_masked || "Ihre E-Mail"}`,
        });
      } else if (d.delivery === "sms") {
        toast.success("Code per SMS gesendet", {
          description: d.phone_masked ? `An ${d.phone_masked}` : "Bitte SMS-Eingang prüfen",
        });
      } else if (d.fallback_reason === "sms_unavailable" && d.delivery === "email") {
        toast.success("Code per E-Mail gesendet", {
          description: `SMS war nicht erreichbar — Code ging an ${d.email_masked || "Ihre E-Mail"}`,
        });
      } else {
        toast.success("Code per E-Mail gesendet", {
          description: d.email_masked ? `An ${d.email_masked}` : "Bitte Postfach prüfen",
        });
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "blocked") {
        setFlow("blocked");
      } else if (detail && typeof detail === "object" && detail.code === "sms_disabled") {
        // Switch UI to email mode and inform user
        setMode("email");
        setSmsEnabled(false);
        toast.error(detail.message || "Bitte mit E-Mail-Adresse registrieren");
      } else {
        toast.error(typeof detail === "string" ? detail : detail?.message || "Anfrage fehlgeschlagen");
      }
    } finally {
      setLoading(false);
    }
  };

  const verify = async () => {
    if (otp.length !== 6) {
      toast.error("Bitte 6-stelligen Code eingeben");
      return;
    }
    setLoading(true);
    try {
      const payload = mode === "phone" ? { phone, code: otp } : { email, code: otp };
      const r = await api.post("/auth/start-verify", payload);
      if (r.data.flow === "login") {
        login(r.data.token, r.data.user);
        toast.success(`Willkommen, ${r.data.user.first_name}!`);
        navigate(postLoginDestination(r.data.user));
      } else {
        // register flow → identity verified, send to profile creation.
        // For Email-Register we pass `email=`, for SMS-Register `phone=`.
        const q = new URLSearchParams();
        if (mode === "email") {
          q.set("email", email);
        } else {
          q.set("phone", phone);
        }
        navigate(`/register/profile?${q.toString()}`);
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : detail?.message || "Code falsch oder abgelaufen");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep("identify");
    setOtp("");
    setMockOtp("");
    setEmailMasked("");
    setPhoneMasked("");
    setFlow("");
    setPendingFirstName("");
  };

  // Demo-Quick-Login: identify → fetch mock_otp → verify in one shot.
  // Used for the collapsible „Demo-Schnellzugang"-section so users don't
  // need to memorize phone numbers. Only works for accounts with on-screen-OTP
  // delivery (Demo-Whitelist / is_test_account=true).
  const [quickLoading, setQuickLoading] = useState("");
  const [demoGroups, setDemoGroups] = useState([]);
  const [demoOpen, setDemoOpen] = useState(false);
  const [demoExpanded, setDemoExpanded] = useState({}); // tenant_id → bool

  useEffect(() => {
    let cancelled = false;
    api.get("/auth/demo-accounts")
      .then((r) => {
        if (cancelled) return;
        const groups = r.data || [];
        setDemoGroups(groups);
        // Auto-expand if there's only ONE tenant — saves a click
        if (groups.length === 1) {
          setDemoExpanded({ [groups[0].tenant_id]: true });
          setDemoOpen(true);
        }
      })
      .catch(() => { /* silent — feature is non-critical */ });
    return () => { cancelled = true; };
  }, []);

  const quickLogin = async (demoPhone, demoLabel) => {
    if (quickLoading || loading) return;
    setQuickLoading(demoPhone);
    try {
      const startRes = await api.post("/auth/start", { phone: demoPhone });
      const d = startRes.data;
      const code = d.mock_otp;
      if (!code) {
        toast.error("Demo-OTP nicht verfügbar (kein mock_otp in Response)");
        return;
      }
      const r = await api.post("/auth/start-verify", { phone: demoPhone, code });
      if (r.data?.token && r.data?.user) {
        login(r.data.token, r.data.user);
        toast.success(`Schnell-Login: ${demoLabel}`);
        navigate(postLoginDestination(r.data.user));
      } else {
        toast.error("Schnell-Login fehlgeschlagen");
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : detail?.message || "Schnell-Login fehlgeschlagen");
    } finally {
      setQuickLoading("");
    }
  };

  const resendOtp = () => {
    if (resendCooldown > 0 || loading) return;
    start(); // re-uses current mode + identifier
  };

  // --- Passkey login (Face ID / Touch ID / Windows Hello) ------------------
  // Uses the email/phone field if filled, otherwise the browser shows ALL
  // passkeys registered for this RP-ID and lets the user pick one.
  const handlePasskeyLogin = async () => {
    if (passkeyLoading) return;
    setPasskeyLoading(true);
    try {
      const identifier = mode === "phone" ? phone.trim() : email.trim();
      const res = await loginWithPasskey(identifier);
      if (res?.ok && res.token && res.user) {
        login(res.token, res.user);
        toast.success(`Willkommen, ${res.user.first_name || ""}!`);
        navigate(postLoginDestination(res.user));
      } else {
        throw new Error("Antwort unvollständig");
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Passkey-Anmeldung fehlgeschlagen";
      toast.error(typeof msg === "string" ? msg : msg.message || "Passkey-Anmeldung fehlgeschlagen", {
        description: "Sie können sich weiterhin per OTP anmelden.",
      });
    } finally {
      setPasskeyLoading(false);
    }
  };

  return (
    <div className="login-page min-h-screen relative flex flex-col items-center justify-center px-6 py-8 overflow-hidden bg-[#09090B]">
      <div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage:
            "radial-gradient(ellipse 80% 60% at 50% 0%, rgba(0,229,117,0.25), transparent 60%), radial-gradient(ellipse 80% 60% at 80% 100%, rgba(0,229,117,0.15), transparent 60%)",
        }}
      />
      <div className="absolute inset-0 bg-[radial-gradient(#27272A_1px,transparent_1px)] [background-size:24px_24px] opacity-30" />

      <div className="relative w-full max-w-md flex flex-col items-center">
        <div className="mb-4 sm:mb-6"><NovatusLogo size="lg" /></div>

        <div data-testid="login-slogan" className="mb-6 sm:mb-8 text-center px-2 max-w-md">
          <p className="font-display text-[18px] sm:text-[22px] leading-snug font-bold tracking-tight">
            <span className="text-zinc-100">Ihre Energiezukunft.</span>{" "}
            <span className="text-[#00E575]">Präzise geplant.</span>{" "}
            <span className="text-zinc-100">Verlässlich umgesetzt.</span>
          </p>
        </div>

        <div className="w-full bg-[#18181B] border border-zinc-800 rounded-2xl p-6 shadow-2xl backdrop-blur-sm">
          {flow === "pending" && (
            <div data-testid="login-pending-banner">
              <div className="flex items-center justify-center w-14 h-14 rounded-full bg-sky-500/15 mx-auto mb-4">
                <Hourglass className="text-sky-400" size={24} />
              </div>
              <h2 className="font-display text-xl font-semibold text-center mb-2">
                {pendingFirstName ? `Hallo ${pendingFirstName}!` : "Anmeldung empfangen"}
              </h2>
              <p className="text-sm text-zinc-400 text-center leading-relaxed mb-5">
                Ihre Registrierung ist bei uns eingegangen.
                Ein Administrator schaltet Ihr Konto in Kürze frei und Sie erhalten
                eine Bestätigung per E-Mail.
              </p>
              <Button onClick={reset} variant="outline" className="w-full border-zinc-700 text-zinc-300">
                Andere Daten verwenden
              </Button>
            </div>
          )}

          {flow === "blocked" && (
            <div className="text-center py-4">
              <div className="w-14 h-14 rounded-full bg-red-500/15 flex items-center justify-center mx-auto mb-3">
                <span className="text-red-400 text-2xl">⊘</span>
              </div>
              <p className="text-sm text-red-200 mb-4">
                Dieses Konto ist gesperrt. Bitte kontaktieren Sie Ihren Administrator.
              </p>
              <Button onClick={reset} variant="outline" className="border-zinc-700 text-zinc-300">
                Andere Daten verwenden
              </Button>
            </div>
          )}

          {step === "identify" && !flow && (
            <>
              <h1 className="font-display text-2xl font-semibold mb-1">Anmelden</h1>
              <p className="text-sm text-zinc-400 mb-5">
                {mode === "phone"
                  ? "Telefonnummer eingeben — der Code kommt per SMS auf Ihr Handy."
                  : "E-Mail eingeben — wir senden Ihnen einen 6-stelligen Login-Code."}
              </p>              {/* Tab Toggle: E-Mail | Handynummer (Handynummer wird ausgeblendet wenn SMS-OTP deaktiviert ist) */}
              {smsEnabled ? (
                <div
                  role="tablist"
                  data-testid="login-mode-toggle"
                  className="flex items-center p-1 mb-5 rounded-xl border border-zinc-800 bg-[#09090B]"
                >
                  <button
                    role="tab"
                    data-testid="login-mode-email"
                    aria-selected={mode === "email"}
                    onClick={() => setMode("email")}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                      mode === "email"
                        ? "bg-[#00E575] text-[#09090B] shadow"
                        : "text-zinc-300 hover:text-zinc-100"
                    }`}
                  >
                    <Mail size={16} /> E-Mail
                  </button>
                  <button
                    role="tab"
                    data-testid="login-mode-phone"
                    aria-selected={mode === "phone"}
                    onClick={() => setMode("phone")}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                      mode === "phone"
                        ? "bg-[#00E575] text-[#09090B] shadow"
                        : "text-zinc-300 hover:text-zinc-100"
                    }`}
                  >
                    <Phone size={16} /> Handynummer
                  </button>
                </div>
              ) : (
                <div
                  data-testid="login-email-only-banner"
                  className="mb-5 flex items-center gap-2 px-3 py-2.5 rounded-xl bg-[#00E575]/10 border border-[#00E575]/30 text-[12px] text-zinc-200 font-medium"
                >
                  <Mail size={14} className="text-[#00E575] shrink-0" />
                  <span>Anmeldung per <strong>E-Mail-Adresse</strong> — SMS-OTP ist aktuell deaktiviert.</span>
                </div>
              )}

              <label className="block text-[12px] font-semibold text-zinc-300 mb-2 uppercase tracking-widest">
                {mode === "phone" ? "Handynummer" : "E-Mail-Adresse"}
              </label>

              {mode === "phone" ? (
                <PhoneInput value={phone} onChange={setPhone} testId="login-phone-input" />
              ) : (
                <Input
                  data-testid="login-email-input"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@firma.de"
                  className="h-12 text-base bg-[#09090B] border-zinc-700 focus:border-[#00E575]"
                />
              )}

              <Button
                data-testid="login-start-btn"
                onClick={() => start()}
                disabled={loading || !canSubmit}
                className="mt-5 h-14 w-full bg-[#00E575] text-[#09090B] font-semibold text-lg rounded-xl hover:bg-[#00C964] glow-green active:scale-[0.97] transition-all disabled:opacity-50"
              >
                {loading ? <Loader2 className="animate-spin" /> : <>Weiter <ArrowRight size={20} className="ml-2" /></>}
              </Button>

              {passkeysAvailable && (
                <>
                  <div className="my-4 flex items-center gap-3 text-[12px] uppercase tracking-widest text-zinc-400">
                    <div className="flex-1 h-px bg-zinc-800" />
                    <span>oder</span>
                    <div className="flex-1 h-px bg-zinc-800" />
                  </div>
                  <Button
                    type="button"
                    data-testid="login-passkey-btn"
                    onClick={handlePasskeyLogin}
                    disabled={passkeyLoading}
                    variant="outline"
                    className="h-12 w-full border border-zinc-700 bg-[#0a0a0c] hover:bg-zinc-900 text-zinc-100 font-semibold rounded-xl flex items-center justify-center gap-2 transition-all active:scale-[0.97] disabled:opacity-50"
                  >
                    {passkeyLoading ? (
                      <Loader2 className="animate-spin" size={18} />
                    ) : (
                      <>
                        <Fingerprint size={18} className="text-[#00E575]" />
                        Mit Face ID / Touch ID anmelden
                      </>
                    )}
                  </Button>
                </>
              )}

              <p className="mt-4 text-center text-sm text-zinc-400 leading-relaxed">
                Noch kein Konto? Sie werden automatisch durch die Registrierung geführt.
              </p>

              {/* Quick-Login: Demo-Schnellzugang (gruppiert nach Mandant)
                  — versteckt, bis der User die geheime „Magic-Number"
                  (DEMO_UNLOCK_CODE) ins Telefon- ODER E-Mail-Feld eingibt.
                  Damit funktioniert die Freischaltung auch auf Produktion,
                  wo SMS-OTP standardmäßig deaktiviert ist. */}
              {demoGroups.length > 0 && (_matchesDemoUnlock(phone) || _matchesDemoUnlock(email)) && (
                <div className="mt-4 pt-4 border-t border-zinc-800/60" data-testid="quick-login-section">
                  <button
                    type="button"
                    onClick={() => setDemoOpen((v) => !v)}
                    data-testid="quick-login-toggle"
                    className="w-full flex items-center justify-between px-1 py-1 group"
                  >
                    <div className="flex items-center gap-1.5 text-[12px] uppercase tracking-widest text-amber-400 font-bold">
                      <Zap size={11} /> Demo-Schnellzugang
                      <span className="text-zinc-500 normal-case tracking-normal font-medium">
                        ({demoGroups.length} {demoGroups.length === 1 ? "Mandant" : "Mandanten"})
                      </span>
                    </div>
                    <ChevronDown
                      size={14}
                      className={`text-zinc-500 group-hover:text-zinc-300 transition-transform ${demoOpen ? "rotate-180" : ""}`}
                    />
                  </button>

                  {demoOpen && (
                    <div className="mt-2 space-y-2" data-testid="quick-login-groups">
                      {demoGroups.map((g) => {
                        const expanded = !!demoExpanded[g.tenant_id];
                        return (
                          <div key={g.tenant_id} className="rounded-xl border border-zinc-800 bg-[#0d0d0e]/60 overflow-hidden">
                            <button
                              type="button"
                              onClick={() => setDemoExpanded((m) => ({ ...m, [g.tenant_id]: !m[g.tenant_id] }))}
                              data-testid={`quick-login-tenant-${g.tenant_slug}`}
                              className="w-full flex items-center justify-between px-3 py-2 hover:bg-zinc-800/30 transition-colors"
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                <div className="h-1.5 w-1.5 rounded-full bg-amber-400 flex-shrink-0" />
                                <div className="text-left min-w-0">
                                  <div className="text-[12px] font-bold text-zinc-100 truncate">{g.tenant_name}</div>
                                  <div className="text-[12px] text-zinc-300">{g.accounts.length} Demo-Account{g.accounts.length !== 1 ? "s" : ""}</div>
                                </div>
                              </div>
                              <ChevronDown size={12} className={`text-zinc-500 transition-transform ${expanded ? "rotate-180" : ""}`} />
                            </button>
                            {expanded && (
                              <div className="px-2 pb-2 space-y-1.5" data-testid={`quick-login-accounts-${g.tenant_slug}`}>
                                {g.accounts.map((u) => {
                                  const isLoading = quickLoading === u.phone;
                                  const fullName = `${u.first_name} ${u.last_name}`.trim() || u.phone;
                                  const roleLabel = roleLabelMap[u.role] || u.role;
                                  const isPriv = ["admin", "telesales_director", "sales_director"].includes(u.role);
                                  const ring = isPriv
                                    ? "border-amber-500/30 hover:border-amber-500/60 hover:bg-amber-500/5"
                                    : "border-emerald-500/30 hover:border-emerald-500/60 hover:bg-emerald-500/5";
                                  const dot = isPriv ? "bg-amber-400" : "bg-emerald-400";
                                  return (
                                    <button
                                      key={u.phone}
                                      type="button"
                                      onClick={() => quickLogin(u.phone, fullName)}
                                      disabled={!!quickLoading || loading}
                                      data-testid={`quick-login-acc-${u.phone}`}
                                      className={`w-full flex items-center justify-between gap-3 px-3 h-11 rounded-lg border ${ring} bg-[#09090B] disabled:opacity-50 disabled:cursor-not-allowed text-left active:scale-[0.98] transition-all`}
                                    >
                                      <div className="flex items-center gap-2 min-w-0">
                                        <span className={`h-2 w-2 rounded-full ${dot} flex-shrink-0`} />
                                        <div className="min-w-0">
                                          <div className="text-[12.5px] font-bold text-zinc-100 truncate">{fullName}</div>
                                          <div className="text-[12px] text-zinc-300 truncate">{roleLabel}</div>
                                        </div>
                                      </div>
                                      {isLoading ? (
                                        <Loader2 size={13} className="animate-spin text-zinc-400 flex-shrink-0" />
                                      ) : (
                                        <ArrowRight size={13} className="text-zinc-500 flex-shrink-0" />
                                      )}
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      <p className="text-[12px] text-zinc-300 text-center leading-snug pt-1">
                        Klick = OTP wird automatisch eingelöst + Login. Nur Demo-Accounts.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Demo-Rundgang Link */}
              <div className="mt-4 pt-4 border-t border-zinc-800/60">
                <Link
                  to="/demo"
                  data-testid="login-demo-link"
                  className="w-full flex items-center justify-center gap-2 h-11 rounded-xl bg-gradient-to-r from-[#00E575]/10 to-[#00E575]/5 border border-[#00E575]/30 hover:border-[#00E575]/60 text-[#00E575] font-semibold text-sm active:scale-[0.97] transition-all"
                >
                  <Sparkles size={14} />
                  Demo-Rundgang ansehen
                  <ArrowRight size={14} />
                </Link>
                <p className="mt-2.5 text-xs text-zinc-300 text-center leading-snug font-medium">
                  15 Schritte · ca. 2 Min. · So funktioniert der Vertriebs-Workflow
                </p>
              </div>
            </>
          )}

          {step === "otp" && (flow === "login" || flow === "register") && (
            <>
              <h1 className="font-display text-2xl font-semibold mb-1">
                {flow === "register"
                  ? (emailMasked || mode === "email" ? "E-Mail bestätigen" : "Nummer bestätigen")
                  : "Willkommen zurück"}
              </h1>
              {mockOtp ? (
                <>
                  <p className="text-sm text-zinc-400 mb-1">
                    Test-Zugang — Code wird hier angezeigt:
                  </p>
                  <div className="mb-3 mt-2 p-3 bg-[#00E575]/10 border border-[#00E575]/30 rounded-lg text-center">
                    <p className="text-[12px] text-[#00E575] font-mono font-semibold tracking-widest uppercase">
                      Ihr Code
                    </p>
                    <p className="text-3xl font-mono font-bold tracking-[0.4em] text-[#00E575] mt-1">
                      {mockOtp}
                    </p>
                  </div>
                </>
              ) : phoneMasked && emailMasked ? (
                <p className="text-sm text-zinc-400 mb-1">
                  Code per SMS gesendet an <span className="text-zinc-200 font-mono">{phoneMasked}</span> und zusätzlich per E-Mail an <span className="text-zinc-200 font-mono">{emailMasked}</span>
                </p>
              ) : phoneMasked ? (
                <p className="text-sm text-zinc-400 mb-1">
                  Code per SMS gesendet an <span className="text-zinc-200 font-mono">{phoneMasked}</span>
                </p>
              ) : emailMasked ? (
                <p className="text-sm text-zinc-400 mb-1">
                  Code per E-Mail gesendet an <span className="text-zinc-200 font-mono">{emailMasked}</span>
                </p>
              ) : (
                <p className="text-sm text-zinc-400 mb-1">
                  Code gesendet. Bitte 6-stelligen Code eingeben.
                </p>
              )}

              <div className="flex justify-center my-5">
                <InputOTP
                  maxLength={6}
                  value={otp}
                  onChange={setOtp}
                  data-testid="login-otp-input"
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  pattern="[0-9]*"
                >
                  <InputOTPGroup className="gap-2">
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <InputOTPSlot
                        key={i}
                        index={i}
                        className="h-14 w-12 text-2xl font-mono border-zinc-700 bg-[#09090B] data-[active=true]:border-[#00E575] data-[active=true]:ring-1 data-[active=true]:ring-[#00E575]"
                      />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>

              {/* Hinweis: Der schnellste Weg ist der Direkt-Login-Link in der
                  SMS (1 Tipp = eingeloggt). Der Code unten ist nur Fallback. */}
              {flow !== "blocked" && !mockOtp && phoneMasked && (
                <p
                  data-testid="login-magic-hint"
                  className="mt-1 mb-3 text-center text-[12px] text-zinc-300 leading-snug"
                >
                  💡 In der SMS tippen Sie einfach auf den Link <span className="font-mono text-zinc-400">„Direkt einloggen“</span> — fertig.
                </p>
              )}

              <Button
                data-testid="login-verify-btn"
                onClick={verify}
                disabled={loading || otp.length !== 6}
                className="h-14 w-full bg-[#00E575] text-[#09090B] font-semibold text-lg rounded-xl hover:bg-[#00C964] glow-green active:scale-[0.97] transition-all disabled:opacity-50"
              >
                {loading ? <Loader2 className="animate-spin" /> : (
                  <>
                    {flow === "register" ? "Code bestätigen" : "Einloggen"}
                    <KeyRound size={18} className="ml-2" />
                  </>
                )}
              </Button>

              <button
                data-testid="login-resend-btn"
                onClick={resendOtp}
                disabled={resendCooldown > 0 || loading}
                className="mt-3 w-full flex items-center justify-center gap-2 text-xs font-medium text-zinc-300 hover:text-[#00E575] disabled:text-zinc-300 disabled:cursor-not-allowed py-2 transition-colors"
              >
                <RefreshCw size={13} className={resendCooldown > 0 ? "" : "group-hover:rotate-180 transition-transform"} />
                {resendCooldown > 0
                  ? `Code erneut senden in ${resendCooldown}s`
                  : "Code erneut senden"}
              </button>

              <button
                data-testid="login-back-btn"
                onClick={reset}
                className="mt-1 w-full text-xs text-zinc-400 hover:text-zinc-200 py-2"
              >
                ← Andere Daten verwenden
              </button>
            </>
          )}
        </div>

        <p className="mt-6 text-sm font-medium text-zinc-300 text-center max-w-xs leading-relaxed">
          Durch die Anmeldung stimmen Sie der Verarbeitung Ihrer Daten nach DSGVO zu.
        </p>

        <div className="mt-4 flex items-center justify-center gap-3 text-sm text-zinc-300">
          <a href="/legal/agb" className="hover:text-[#00E575] transition-colors font-medium" data-testid="footer-link-agb">AGB</a>
          <span className="text-zinc-600">·</span>
          <a href="/legal/datenschutz" className="hover:text-[#00E575] transition-colors font-medium" data-testid="footer-link-datenschutz">Datenschutz</a>
          <span className="text-zinc-600">·</span>
          <a href="/legal/impressum" className="hover:text-[#00E575] transition-colors font-medium" data-testid="footer-link-impressum">Impressum</a>
        </div>
      </div>
    </div>
  );
}
